/**
 * S2 — Areas. The area list; each card opens that area's counts.
 *
 * `GET /count/areas` returns **exactly** the areas this principal may access, so
 * a manager assigned to one area sees one. CLAUDE.md rule 2 is therefore
 * structural rather than a filter this screen applies — there is no client-side
 * narrowing here to get wrong, and none to be bypassed by editing a URL.
 */
import { Link } from "react-router-dom";

import { useCountAreas } from "../api/queries";
import { useAuth } from "../auth/AuthContext";

export function Areas() {
  const { signedIn, devUpn } = useAuth();
  const { data, isLoading, error } = useCountAreas(signedIn);

  if (!signedIn) {
    return (
      <section className="card">
        <h2>Not signed in</h2>
        <p>
          <Link to="/">Sign in</Link> to see your count areas.
        </p>
      </section>
    );
  }

  if (isLoading) return <p className="muted">Loading your count areas…</p>;

  if (error) {
    return (
      <section className="card error">
        <h2>Could not load areas</h2>
        <p>{(error as Error).message}</p>
        <p className="muted">
          A 403 here means the signed-in user is not in <code>access.yaml</code>,
          which is a config question rather than an app one.
        </p>
      </section>
    );
  }

  const areas = data?.areas ?? [];

  return (
    <section>
      <h2>Count areas</h2>
      <p className="muted">
        {areas.length === 1
          ? "You are assigned to one count area."
          : `You are assigned to ${areas.length} count areas.`}{" "}
        {devUpn && <>Signed in as {devUpn} (dev).</>}
      </p>

      {areas.length === 0 ? (
        <div className="card">
          <p>
            No count areas are assigned to this user. That is an{" "}
            <code>access.yaml</code> entry, not a missing screen.
          </p>
        </div>
      ) : (
        <ul className="area-list">
          {areas.map((area) => (
            <li key={area.id}>
              <Link
                className="area-card"
                to={`/areas/${encodeURIComponent(area.id)}`}
                data-testid={`area-${area.id}`}
              >
                <div className="area-card__name">{area.display_name}</div>
                <dl className="area-card__facts">
                  <div>
                    <dt>Company</dt>
                    <dd>{area.company_name}</dd>
                  </div>
                  <div>
                    <dt>Location</dt>
                    <dd>
                      <code>{area.location_code}</code>
                    </dd>
                  </div>
                  <div>
                    <dt>Type</dt>
                    {/* Shown because it decides which dollar bar this area's swing
                        flags are measured against — $500 wholesale, $250 retail
                        (Kevin, 2026-08-18). */}
                    <dd>{area.area_type}</dd>
                  </div>
                </dl>
                <p className="muted small">
                  Resume a count, look at someone else's, or start a new one.
                </p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
